import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronsUpDown, Search } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
  /** Kleiner Zusatz rechts, z.B. eine Adresse oder Anzahl. */
  hint?: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Steht im Auslöser, solange kein Eintrag zum Wert passt. */
  placeholder?: string;
  /** `field` für Formulare, `button` für Werkzeugleisten. */
  variant?: "field" | "button";
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Suchfeld über der Liste; ohne Angabe ab acht Einträgen. */
  searchable?: boolean;
}

/**
 * Auswahlfeld im Stil des Admin (USelect/USelectMenu).
 *
 * Die Liste hängt per Portal am Dokument und wird fest positioniert: Die
 * Spalten der Oberfläche schneiden mit overflow:hidden ab, eine eingebettete
 * Liste verschwände am Rand. Bedienung wie beim nativen Element — Pfeiltasten,
 * Enter, Esc, Pos1/Ende, Tippen springt zum passenden Eintrag.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = "Auswählen …",
  variant = "field",
  disabled,
  id,
  className,
  style,
  searchable,
  "aria-label": ariaLabel,
}: Props) {
  const listId = useId();
  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const suche = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [aktiv, setAktiv] = useState(-1);
  const [filter, setFilter] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number; maxHeight: number; oben: boolean } | null>(null);
  const tippen = useRef({ text: "", zeit: 0 });

  const mitSuche = searchable ?? options.length > 8;
  const gewaehlt = options.find((o) => o.value === value);

  const sichtbar = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return options;
    return options.filter((o) => o.label.toLowerCase().includes(f) || o.hint?.toLowerCase().includes(f));
  }, [options, filter]);

  const platzieren = useCallback(() => {
    const el = trigger.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const unten = window.innerHeight - r.bottom - 12;
    const drueber = r.top - 12;
    // Nach oben aufklappen, wenn unten kein Platz ist und oben mehr.
    const oben = unten < 220 && drueber > unten;
    const width = Math.max(r.width, variant === "button" ? 220 : 0);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    setPos({
      left: Math.max(8, left),
      top: oben ? r.top - 4 : r.bottom + 4,
      width,
      maxHeight: Math.min(320, oben ? drueber : unten),
      oben,
    });
  }, [variant]);

  const oeffnen = () => {
    if (disabled) return;
    setFilter("");
    const idx = options.findIndex((o) => o.value === value);
    setAktiv(idx >= 0 ? idx : options.findIndex((o) => !o.disabled));
    platzieren();
    setOpen(true);
  };

  const schliessen = (fokus = true) => {
    setOpen(false);
    if (fokus) trigger.current?.focus();
  };

  const waehlen = (o: SelectOption | undefined) => {
    if (!o || o.disabled) return;
    schliessen();
    if (o.value !== value) onChange(o.value);
  };

  // Mitwandern beim Scrollen und bei Größenänderung, schließen beim Klick daneben.
  useLayoutEffect(() => {
    if (!open) return;
    const neu = (e?: Event) => {
      // Scrollen in der eigenen Liste verschiebt den Auslöser nicht.
      if (e && panel.current?.contains(e.target as Node)) return;
      // Ist der Auslöser aus dem Blick gescrollt, hätte die Liste keinen Anker mehr.
      const r = trigger.current?.getBoundingClientRect();
      if (e?.type === "scroll" && r && (r.bottom < 0 || r.top > window.innerHeight)) {
        schliessen(false);
        return;
      }
      platzieren();
    };
    const daneben = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || trigger.current?.contains(t)) return;
      schliessen(false);
    };
    window.addEventListener("resize", neu);
    window.addEventListener("scroll", neu, true);
    document.addEventListener("mousedown", daneben);
    return () => {
      window.removeEventListener("resize", neu);
      window.removeEventListener("scroll", neu, true);
      document.removeEventListener("mousedown", daneben);
    };
  }, [open, platzieren]);

  useEffect(() => {
    if (open && mitSuche) suche.current?.focus();
  }, [open, mitSuche]);

  // Aktiven Eintrag in Sicht halten.
  useEffect(() => {
    if (!open || aktiv < 0) return;
    panel.current?.querySelector<HTMLElement>(`[data-index="${aktiv}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, aktiv]);

  const schritt = (von: number, delta: number) => {
    if (sichtbar.length === 0) return -1;
    let i = von;
    for (let n = 0; n < sichtbar.length; n++) {
      i = (i + delta + sichtbar.length) % sichtbar.length;
      if (!sichtbar[i]!.disabled) return i;
    }
    return von;
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        oeffnen();
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); setAktiv((a) => schritt(a, 1)); break;
      case "ArrowUp": e.preventDefault(); setAktiv((a) => schritt(a, -1)); break;
      case "Home": e.preventDefault(); setAktiv(schritt(-1, 1)); break;
      case "End": e.preventDefault(); setAktiv(schritt(sichtbar.length, -1)); break;
      case "Enter": e.preventDefault(); waehlen(sichtbar[aktiv]); break;
      case "Escape": e.preventDefault(); e.stopPropagation(); schliessen(); break;
      // Fokus zurück auf den Auslöser; der Browser springt von dort weiter.
      // Ohne das landete der Fokus hinter dem Portal am Ende des Dokuments.
      case "Tab": schliessen(); break;
      default:
        // Ohne Suchfeld springt Tippen zum ersten passenden Eintrag.
        if (!mitSuche && e.key.length === 1) {
          const jetzt = Date.now();
          const t = tippen.current;
          t.text = jetzt - t.zeit > 700 ? e.key.toLowerCase() : t.text + e.key.toLowerCase();
          t.zeit = jetzt;
          const i = sichtbar.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(t.text));
          if (i >= 0) setAktiv(i);
        }
    }
  };

  const klassen = ["ui-select", variant === "button" ? "btn" : "select", className].filter(Boolean).join(" ");

  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        className={klassen}
        style={style}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && !mitSuche && aktiv >= 0 ? `${listId}-${aktiv}` : undefined}
        data-open={open}
        onClick={() => (open ? schliessen() : oeffnen())}
        onKeyDown={onKey}
      >
        <span className="ui-select-wert" data-leer={!gewaehlt}>
          {gewaehlt ? gewaehlt.label : placeholder}
        </span>
        <ChevronsUpDown className="ui-select-pfeil" aria-hidden="true" />
      </button>

      {open && pos && createPortal(
        <div
          ref={panel}
          className="ui-select-panel"
          data-oben={pos.oben}
          style={{
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
            ...(pos.oben ? { bottom: window.innerHeight - pos.top } : { top: pos.top }),
          }}
        >
          {mitSuche && (
            <div className="ui-select-suche">
              <Search aria-hidden="true" />
              <input
                ref={suche}
                value={filter}
                placeholder="Suchen …"
                aria-label="Einträge filtern"
                aria-controls={listId}
                aria-activedescendant={aktiv >= 0 ? `${listId}-${aktiv}` : undefined}
                onChange={(e) => { setFilter(e.target.value); setAktiv(0); }}
                onKeyDown={onKey}
              />
            </div>
          )}
          <ul id={listId} role="listbox" className="ui-select-liste" aria-label={ariaLabel}>
            {sichtbar.length === 0 && <li className="ui-select-leer">Keine Treffer</li>}
            {sichtbar.map((o, i) => (
              <li
                key={`${o.value}-${i}`}
                id={`${listId}-${i}`}
                role="option"
                data-index={i}
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                data-aktiv={i === aktiv}
                className="ui-select-option"
                onMouseEnter={() => !o.disabled && setAktiv(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => waehlen(o)}
              >
                <span className="ui-select-label">{o.label}</span>
                {o.hint && <span className="ui-select-hint">{o.hint}</span>}
                {o.value === value && <Check className="ui-select-check" aria-hidden="true" />}
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
